import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  Wallet,
  Search,
  Heart,
  CreditCard,
  UserCircle,
} from "lucide-react";

const navItems = [
  { labelKey: "mobile-bottom-nav.wallet", icon: Wallet, href: "/wallet", matchPaths: ["/wallet", "/"] },
  { labelKey: "mobile-bottom-nav.discover", icon: Search, href: "/wallet/discover", matchPaths: ["/wallet/discover"] },
  { labelKey: "mobile-bottom-nav.community", icon: Heart, href: "/community-funds", matchPaths: ["/community-funds", "/community", "/conditions"] },
  { labelKey: "mobile-bottom-nav.plans", icon: CreditCard, href: "/payment-plans", matchPaths: ["/payment-plans", "/subscriptions"] },
  { labelKey: "mobile-bottom-nav.profile", icon: UserCircle, href: "/profile", matchPaths: ["/profile", "/settings"] },
];

export function MobileBottomNav() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: pendingInvitations } = useQuery<any[]>({
    queryKey: ["/api/user/pending-invitations"],
    enabled: !!user,
    refetchInterval: 60000,
  });

  const pendingCount = pendingInvitations?.length || 0;

  const isActive = (item: typeof navItems[0]) => {
    return item.matchPaths.some(path => {
      if (path === "/wallet") {
        return location === "/wallet" || location === "/";
      }
      return location === path || location.startsWith(path + "/");
    });
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border safe-area-bottom"
      data-testid="nav-mobile-bottom"
    >
      <div className="flex items-center justify-around gap-1 py-1.5 px-2">
        {navItems.map((item) => {
          const active = isActive(item);
          const itemSlug = item.labelKey.split('.').pop()!;
          const showBadge = itemSlug === "wallet" && pendingCount > 0;
          return (
            <Link key={item.href} href={item.href} className="flex-1">
              <div
                className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg transition-colors cursor-pointer relative w-full min-h-[44px] ${
                  active
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
                data-testid={`nav-mobile-${itemSlug}`}
                aria-current={active ? "page" : undefined}
              >
                <div className="relative">
                  <item.icon className={`h-5 w-5 ${active ? "stroke-[2.5]" : ""}`} />
                  {showBadge && (
                    <span 
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1"
                      data-testid="badge-pending-invitations-mobile"
                    >
                      {pendingCount > 9 ? "9+" : pendingCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] leading-tight ${active ? "font-semibold" : "font-medium"}`}>
                  {t(item.labelKey)}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
